import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ChatWidgetInputBase } from '../chat-widget-input-base';

export interface ChatAddressOption {
  id: string;
  description?: string | null;
  numberOrName?: string | null;
  street?: string | null;
  town?: string | null;
  county?: string | null;
  postcode?: string | null;
  countryCode?: string | null;
  specialInstructions?: string | null;
  addressLine?: string | null;
  cityName?: string | null;
  postalCode?: string | null;
}

export interface ChatAddressValue {
  numberOrName: string;
  street: string;
  town: string;
  county: string;
  postcode: string;
  countryCode: string;
  specialInstructions: string;
  addressLine: string;
  cityName: string;
  postalCode: string;
}

export type ChatAddressAnswer =
  | { kind: 'saved'; address: ChatAddressValue; description: string }
  | { kind: 'manual'; address: ChatAddressValue };

const NONE_ID = '__none__';

@Component({
  selector: 'app-chat-address-input',
  standalone: false,
  templateUrl: './chat-address-input.component.html',
  styleUrls: ['./chat-address-input.component.scss']
})
export class ChatAddressInputComponent extends ChatWidgetInputBase implements OnChanges {
  @Input() savedAddresses: ChatAddressOption[] = [];
  @Input() savedValue: Partial<ChatAddressValue> | null = null;
  @Input() required = true;
  @Input() fieldLabel = 'Booking address';
  @Input() showLabel = true;
  @Input() enterpriseLocation = false;
  @Input() manualOnly = false;
  @Output() answer = new EventEmitter<ChatAddressAnswer>();

  readonly NONE_ID = NONE_ID;
  selectedId = '';
  chosenSummary: string | null = null;
  address: ChatAddressValue = this.emptyAddress();

  ngOnChanges(changes: SimpleChanges): void {
    if (this.onlyLockedChanged(changes)) return;
    this.selectedId = this.manualOnly ? NONE_ID : '';
    this.chosenSummary = null;
    this.address = {
      ...this.emptyAddress(),
      ...(this.savedValue ?? {})
    };
  }

  get showManualEntry(): boolean {
    return this.selectedId === NONE_ID;
  }

  get selectedSavedAddress(): ChatAddressOption | null {
    if (!this.selectedId || this.selectedId === NONE_ID) return null;
    return this.savedAddresses.find((address) => String(address.id) === this.selectedId) ?? null;
  }

  get isValid(): boolean {
    return this.requiredAddressFields().every(
      (field) => String(this.address[field as keyof ChatAddressValue] ?? '').trim().length > 0
    );
  }

  onDropdownChange(id: string): void {
    if (this.locked) return;
    this.selectedId = id;
    const saved = this.selectedSavedAddress;
    if (saved) {
      this.address = this.toAddress(saved);
      if (!this.isAddressComplete(this.address)) {
        this.selectedId = NONE_ID;
      }
    }
  }

  onContinue(): void {
    if (this.locked || !this.isValid) return;
    const saved = this.selectedSavedAddress;
    if (saved) {
      this.chosenSummary = this.formatAddress(saved);
      this.answer.emit({
        kind: 'saved',
        address: this.toAddress(saved),
        description: saved.description || this.formatAddress(saved)
      });
      return;
    }
    this.chosenSummary = this.formatAddress(this.address);
    this.answer.emit({ kind: 'manual', address: { ...this.address } });
  }

  formatAddress(address: ChatAddressOption | ChatAddressValue): string {
    const fields = this.enterpriseLocation
      ? [
          address.addressLine,
          address.cityName,
          address.countryCode,
          address.postalCode,
          address.specialInstructions
        ]
      : [
          address.numberOrName,
          address.street,
          address.town,
          address.county,
          address.postcode,
          address.countryCode
        ];
    return fields.filter(Boolean).join(', ');
  }

  idToString(id: string | number): string {
    return String(id);
  }

  private toAddress(address: ChatAddressOption): ChatAddressValue {
    return {
      numberOrName: address.numberOrName ?? '',
      street: address.street ?? '',
      town: address.town ?? '',
      county: address.county ?? '',
      postcode: address.postcode ?? '',
      countryCode: address.countryCode ?? '',
      specialInstructions: address.specialInstructions ?? '',
      addressLine: address.addressLine ?? address.numberOrName ?? '',
      cityName: address.cityName ?? address.town ?? '',
      postalCode: address.postalCode ?? address.postcode ?? ''
    };
  }

  private isAddressComplete(address: ChatAddressValue): boolean {
    return this.requiredAddressFields().every(
      (field) => String(address[field as keyof ChatAddressValue] ?? '').trim().length > 0
    );
  }

  private requiredAddressFields(): string[] {
    return this.enterpriseLocation
      ? ['addressLine', 'cityName', 'countryCode', 'postalCode', 'specialInstructions']
      : ['numberOrName', 'street', 'town', 'county', 'postcode', 'countryCode'];
  }

  private emptyAddress(): ChatAddressValue {
    return {
      numberOrName: '',
      street: '',
      town: '',
      county: '',
      postcode: '',
      countryCode: '',
      specialInstructions: '',
      addressLine: '',
      cityName: '',
      postalCode: ''
    };
  }
}
